import { useEffect, useState } from 'react';
import { call } from './api';

/** Addons per profiel (Stremio voor video, Eclipse voor muziek). Adressen en tokens blijven op de server. */

export type SourceOrder = 'plex-first' | 'addons-first' | 'addons-only' | 'plex-only';

export const SOURCE_ORDERS: [SourceOrder, string, string][] = [
  ['plex-first', 'Eerst Plex', 'Plex als het er is, anders je addons'],
  ['addons-first', 'Eerst addons', 'Bronnen uit je addons bovenaan, Plex daarna'],
  ['plex-only', 'Alleen Plex', 'Addons niet als afspeelbron gebruiken'],
  ['addons-only', 'Alleen addons', 'Plex niet gebruiken om af te spelen'],
];

export interface CatalogDef { key: string; name: string; type: string; searchOnly?: boolean }

export interface Addon {
  id: string;
  manifestId: string;
  name: string;
  description: string | null;
  icon: string | null;
  version: string;
  kind: 'video' | 'music';
  resources: string[];
  types: string[];
  idPrefixes: string[] | null;
  catalogs: CatalogDef[];
  widgets: boolean;
  streams: boolean;
  order: number;
  addedAt: number;
}

export interface ManifestInfo extends Omit<Addon, 'id' | 'widgets' | 'streams' | 'order' | 'addedAt'> {
  registryURL: string | null;
  configurationRequired: boolean;
}

export interface StoreEntry { id: string; name: string; description: string | null; author: string | null; icon: string | null; setupUrl: string | null; manifestUrl: string | null }

export interface AddonMeta {
  id: string;
  type: string;
  name: string;
  poster: string | null;
  posterShape: string;
  background: string | null;
  logo: string | null;
  description: string | null;
  releaseInfo: string | null;
  imdbRating: string | null;
  genres: string[];
}

export interface AddonVideo { id: string; title: string; season: number | null; episode: number | null; released: string | null; thumbnail: string | null; overview: string | null }

export interface StreamOption {
  key: string;
  addonId: string;
  addonName: string;
  name: string;
  title: string;
  quality: string | null;
  size: number | null;
  webReady: boolean;
  traits?: { hdr: 'dv' | 'hdr' | null; codec: string | null; audio: string | null; cached: boolean | null; seeders: number | null; nl?: 'audio' | 'subs' | null };
  score?: number;
  fp?: string;
  why?: string[];
  pinned?: boolean;
  blocked?: { auto: boolean } | null;
  /** Vooraf getest door de server: true = antwoordt met video */
  verified?: boolean;
}

export interface PickPrefs { autoPick: boolean; failover: boolean; prefetchNext: boolean; perQuality?: number }

export const addonsApi = {
  list: () => call<{ allowed: boolean; manage?: boolean; sourceOrder: SourceOrder; addons: Addon[]; iptvVod?: boolean }>('/api/addons'),
  check: (url: string) => call<{ addon: ManifestInfo }>('/api/addons/check', { method: 'POST', body: JSON.stringify({ url }) }),
  add: (url: string) => call<{ addon: Addon }>('/api/addons', { method: 'POST', body: JSON.stringify({ url }) }),
  patch: (id: string, b: { widgets?: boolean; streams?: boolean; order?: number }) => call<{ addon: Addon }>(`/api/addons/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
  remove: (id: string) => call<{ ok: true }>(`/api/addons/${id}`, { method: 'DELETE' }),
  store: (url: string) => call<{ name: string; addons: StoreEntry[] }>('/api/addons/store', { method: 'POST', body: JSON.stringify({ url }) }),
  catalog: (id: string, key: string, opts: { skip?: number; search?: string } = {}) => {
    const q = new URLSearchParams({ key });
    if (opts.skip) q.set('skip', String(opts.skip));
    if (opts.search) q.set('search', opts.search);
    return call<{ items: AddonMeta[] }>(`/api/addons/${id}/catalog?${q}`);
  },
  meta: (id: string, type: string, metaId: string) => call<{ meta: AddonMeta & { videos: AddonVideo[] } }>(`/api/addons/${id}/meta/${encodeURIComponent(type)}/${encodeURIComponent(metaId)}`),
  streams: (type: string, id: string, addon?: string) => {
    const q = new URLSearchParams({ type, id });
    if (addon) q.set('addon', addon);
    return call<{ sourceOrder: SourceOrder; pick?: PickPrefs; streams: StreamOption[]; errors: { addon: string; message: string }[]; pending?: string[] }>(`/api/addons/streams?${q}`);
  },
  live: () => call<{ items: (AddonMeta & { addonId: string; addonName: string })[] }>('/api/addons/live'),
  choice: (id: string, fp: string, action: 'pin' | 'unpin' | 'block' | 'unblock', label: string) =>
    call<{ ok: true }>('/api/addons/source-choice', { method: 'POST', body: JSON.stringify({ id, fp, action, label }) }),
  failed: (id: string, fp: string, label: string) =>
    call<{ blocked: boolean }>('/api/addons/source-fail', { method: 'POST', body: JSON.stringify({ id, fp, label }) }).catch(() => ({ blocked: false })),
  play: (key: string, opts: { transcode?: boolean; start?: number; direct?: boolean; live?: boolean; hevc?: boolean } = {}) =>
    call<{ sessionId: string | null; url: string | null; direct: string; external?: boolean; transcoded?: boolean; duration?: number | null; start?: number }>('/api/addons/play', { method: 'POST', body: JSON.stringify({ key, ...opts }) }),
  stop: (sid: string) => call<{ ok: true }>(`/api/addons/hls/${sid}/stop`, { method: 'POST' }).catch(() => undefined),
};

let cached: Promise<{ allowed: boolean; manage?: boolean; sourceOrder: SourceOrder; addons: Addon[]; iptvVod?: boolean }> | null = null;

/** Mijn addons (gedeeld tussen schermen; opnieuw laden na wijzigen met refreshAddons). */
export function loadAddons() {
  if (!cached) {
    cached = addonsApi.list().catch(() => {
      cached = null;
      return { allowed: false, sourceOrder: 'plex-first' as SourceOrder, addons: [] };
    });
  }
  return cached;
}

export function refreshAddons() {
  cached = null;
  listeners.forEach((l) => l());
}

const listeners = new Set<() => void>();

export function useAddons() {
  const [state, setState] = useState<{ allowed: boolean; manage?: boolean; sourceOrder: SourceOrder; addons: Addon[]; iptvVod?: boolean } | null>(null);
  useEffect(() => {
    const load = () => void loadAddons().then(setState);
    load();
    listeners.add(load);
    return () => {
      listeners.delete(load);
    };
  }, []);
  return state;
}

export function formatSize(bytes: number | null): string | null {
  if (!bytes) return null;
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(1).replace('.', ',')} GB` : `${Math.round(bytes / 1024 ** 2)} MB`;
}

/** Stremio-id van een Plex-titel: imdb uit de Plex-guids (film "tt…", aflevering "tt…:S:E"). */
export function stremioIdFor(item: { type: string; Guid?: { id: string }[]; parentIndex?: number; index?: number }, showGuids?: { id: string }[]): { type: 'movie' | 'series'; id: string } | null {
  const imdb = (g?: { id: string }[]) => g?.find((x) => x.id.startsWith('imdb://'))?.id.slice(7) ?? null;
  if (item.type === 'movie') {
    const id = imdb(item.Guid);
    return id ? { type: 'movie', id } : null;
  }
  if (item.type === 'episode') {
    const id = imdb(showGuids);
    return id && item.parentIndex != null && item.index != null ? { type: 'series', id: `${id}:${item.parentIndex}:${item.index}` } : null;
  }
  return null;
}

// ---------- Kijkvoortgang (addon-titels) ----------

/** Wat er speelt: om voortgang, Verder kijken en Trakt/SIMKL bij te houden. */
export interface WatchCtx {
  id: string;
  type: 'movie' | 'series';
  metaId: string;
  addonId?: string;
  name: string;
  epTitle?: string;
  season?: number;
  episode?: number;
  poster?: string;
  background?: string;
  /** Titel staat ook in Plex: voortgang gaat naar Plex */
  ratingKey?: string;
  /** TMDB-id (titels die niet uit een addon-catalogus komen) */
  tmdbId?: number;
}

export interface ProgressItem extends WatchCtx {
  position: number;
  duration: number;
  updatedAt: number;
  watched: boolean;
  source?: { addon: string; name: string };
}

/** Verder kijken van Trakt: elders half gekeken, of de volgende aflevering. */
export interface UpNext {
  type: 'movie' | 'tv';
  tmdbId: number;
  imdb: string | null;
  title: string;
  year: number | null;
  poster: string | null;
  backdrop: string | null;
  season?: number;
  episode?: number;
  epTitle?: string;
  progress?: number;
  at: number;
  ratingKey: string | null;
  why: 'paused' | 'next';
}

export const progressApi = {
  resume: (id: string, ratingKey?: string) => {
    const q = new URLSearchParams({ id });
    if (ratingKey) q.set('ratingKey', ratingKey);
    return call<{ position: number; duration: number; source: { addon: string; name: string } | null }>(`/api/progress/resume?${q}`);
  },
  of: (metaId: string) => call<{ items: { id: string; season?: number; episode?: number; position: number; duration: number; watched: boolean; updatedAt: number; source: { addon: string; name: string } | null }[] }>(`/api/progress/of?metaId=${encodeURIComponent(metaId)}`),
  report: (b: { ctx: WatchCtx; position: number; duration: number; state: 'playing' | 'paused' | 'stopped'; source?: { addon: string; name: string } }, keepalive = false) =>
    call<{ ok: boolean; watched: boolean }>('/api/progress', { method: 'PUT', body: JSON.stringify(b), keepalive }).catch(() => undefined),
  continue: () => call<{ items: ProgressItem[] }>('/api/progress/continue'),
  upnext: () => call<{ items: UpNext[] }>('/api/progress/upnext'),
  hideUpNext: (type: 'movie' | 'tv', tmdbId: number) => call<{ ok: true }>('/api/progress/upnext/hide', { method: 'POST', body: JSON.stringify({ type, tmdbId }) }),
  remove: (id: string) => call<{ ok: true }>(`/api/progress/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  watched: (id: string, watched: boolean) => call<{ ok: boolean }>(`/api/progress/${encodeURIComponent(id)}/watched`, { method: 'POST', body: JSON.stringify({ watched }) }),
};

export function encodeCtx(c: WatchCtx): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(c)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeCtx(s: string | null): WatchCtx | null {
  if (!s) return null;
  try {
    return JSON.parse(decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))))) as WatchCtx;
  } catch {
    return null;
  }
}

// ---------- Reservebronnen (bij een fout de volgende) ----------

export interface PlayQueue {
  title: string;
  /** Stremio-id van wat er speelt (voor het melden van een bron die niet werkt) */
  titleId?: string;
  ctx?: WatchCtx;
  list: StreamOption[];
  i: number;
}

const QUEUE_KEY = 'tm-play-queue';

export function setPlayQueue(q: PlayQueue | null): void {
  try {
    if (q) sessionStorage.setItem(QUEUE_KEY, JSON.stringify({ ...q, list: q.list.slice(0, 8) }));
    else sessionStorage.removeItem(QUEUE_KEY);
  } catch {
    // privévenster e.d.: dan geen reserves
  }
}

export function getPlayQueue(): PlayQueue | null {
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as PlayQueue) : null;
  } catch {
    return null;
  }
}

/** Volgende aflevering (Stremio-id "tt…:seizoen:aflevering"): eerst dit seizoen, anders seizoen +1. */
export async function findNextEpisode(ctx: WatchCtx): Promise<{ ctx: WatchCtx; streams: StreamOption[] } | null> {
  if (ctx.type !== 'series' || ctx.season == null || ctx.episode == null) return null;
  const tries: [number, number][] = [[ctx.season, ctx.episode + 1], [ctx.season + 1, 1]];
  for (const [season, episode] of tries) {
    const id = `${ctx.metaId}:${season}:${episode}`;
    const r = await addonsApi.streams('series', id).catch(() => null);
    if (r?.streams.length) {
      return { ctx: { ...ctx, id, season, episode, epTitle: undefined, ratingKey: undefined }, streams: r.streams };
    }
  }
  return null;
}
